'use client';

import { subscriptionsApi, type CurrentSubscription } from '@/lib/api/subscriptions';
import { useCreditsStore } from '@/lib/stores/credits-store';
import { useFeatureFlagsStore } from '@/lib/stores/feature-flags-store';
import { pricingModal } from '@/lib/stores/pricing-modal-store';
import type { MembershipRequirement } from '@/lib/membership-requirement';
import { projectApi } from '@/lib/api/projects';
import { translateClient } from '@/lib/i18n/client';
import { readEntitlementFailure, entitlementPurchaseIntent, entitlementRequiredCredits, entitlementAvailableCredits, isMembershipFailure } from './entitlement-failure';

export type SubscriptionFeatureKey =
  | 'model_access'
  | string;

type PlanFeatures = Record<string, unknown>;

export interface SubscriptionEntitlementSnapshot {
  subscription: CurrentSubscription | null;
  currentPlan: string | null;
  features: PlanFeatures;
}

export interface RequireSubscriptionFeatureOptions {
  onUnlocked?: () => void | Promise<void>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readDetail(input: unknown): Record<string, unknown> | null {
  if (!isRecord(input)) return null;

  // Preserve the business code/message on the outer ApiError while flattening
  // FastAPI detail, normalized ApiError.details, and API envelope error layers.
  // Returning `details` directly loses an outer PROJECT_*_LIMIT_EXCEEDED code.
  const flattened: Record<string, unknown> = {};
  for (const key of ['detail', 'details', 'error'] as const) {
    const nested = readDetail(input[key]);
    if (nested) {
      Object.assign(flattened, nested);
    }
  }
  return { ...flattened, ...input };
}

export function isSubscriptionEntitlementError(input: unknown): boolean {
  return isMembershipFailure(readEntitlementFailure(input));
}

export function getMembershipLimitErrorCode(input: unknown): string | null {
  const code = readEntitlementFailure(input)?.code ?? '';
  return code === 'MEMBERSHIP_USE_EXHAUSTED'
    || code === 'PROJECT_LIMIT_EXCEEDED'
    || code === 'PROJECT_ITEM_LIMIT_EXCEEDED'
    ? code
    : null;
}

export function getSubscriptionEntitlementFeatureKey(input: unknown): string | undefined {
  const detail = readEntitlementFailure(input)?.details ?? readDetail(input);
  if (!detail) return undefined;
  if (typeof detail.feature_key === 'string' && detail.feature_key.trim()) return detail.feature_key;
  if (typeof detail.model_key === 'string' && detail.model_key.trim()) return detail.model_key;
  return undefined;
}

export async function getSubscriptionEntitlementSnapshot(): Promise<SubscriptionEntitlementSnapshot | null> {
  const response = await subscriptionsApi.getCurrent();
  if (response.success !== true || response.error || !response.data?.subscription) {
    return null;
  }

  const subscription = response.data.subscription;
  const plan = response.data.entitlement?.effective_plan ?? subscription.plan;
  return {
    subscription,
    currentPlan: plan?.slug ? String(plan.slug).toLowerCase() : null,
    features: (plan?.features || {}) as PlanFeatures,
  };
}

export function canUseSubscriptionFeature(
  features: PlanFeatures | null | undefined,
  featureKey: SubscriptionFeatureKey,
): boolean {
  if (!features) return false;
  const booleanFeatureKeys = [featureKey];
  for (const key of booleanFeatureKeys) {
    if (key in features) {
      return features[key] === true;
    }
  }
  const allowedModels = features.allowed_models;
  if (Array.isArray(allowedModels) && allowedModels.every((item) => typeof item === 'string')) {
    return allowedModels.includes('*') || allowedModels.includes(featureKey);
  }
  return false;
}

export async function refreshSubscriptionEntitlementState(): Promise<void> {
  await Promise.allSettled([
    useCreditsStore.getState().refetchCredits(),
    useFeatureFlagsStore.getState().refetchFlags(),
  ]);
}

export function openLockedFeaturePaywall(options?: {
  featureKey?: SubscriptionFeatureKey;
  featureLabel?: string;
  requirementKind?: 'model' | 'feature';
  onUnlocked?: () => void | Promise<void>;
}): void {
  pricingModal.open({
    triggerReason: 'feature_locked',
    defaultTab: 'upgrade',
    quotaType: options?.featureKey,
    featureLabel: options?.featureLabel,
    membershipRequirement: options?.featureKey ? { kind: options.requirementKind ?? 'model', key: options.featureKey } : undefined,
    onSuccess: async () => {
      await refreshSubscriptionEntitlementState();
      await options?.onUnlocked?.();
    },
  });
}

export async function requireSubscriptionFeature(
  featureKey: SubscriptionFeatureKey,
  options?: RequireSubscriptionFeatureOptions,
): Promise<boolean> {
  const snapshot = await getSubscriptionEntitlementSnapshot();
  if (!snapshot) {
    throw { code: 'SERVICE_UNAVAILABLE', message: translateClient('pricing', 'accessCheckFailed'), retryable: true };
  }
  if (canUseSubscriptionFeature(snapshot.features, featureKey)) {
    return true;
  }

  openLockedFeaturePaywall({
    featureKey,
    requirementKind: typeof snapshot.features[featureKey] === 'boolean' ? 'feature' : 'model',
    onUnlocked: options?.onUnlocked,
  });
  return false;
}

/** Present only at the user-action boundary, never from background API reads. */
export function handleSubscriptionEntitlementApiError(params: {
  error: unknown;
  featureLabel?: string;
}): boolean {
  const failure = readEntitlementFailure(params.error);
  if (!isMembershipFailure(failure) || entitlementPurchaseIntent(failure) !== 'upgrade') return false;
  const detail = failure?.details;
  const limitCode = getMembershipLimitErrorCode(params.error);
  const source = limitCode === 'PROJECT_LIMIT_EXCEEDED'
    ? 'project_limit'
    : limitCode === 'PROJECT_ITEM_LIMIT_EXCEEDED'
      ? 'project_item_limit'
      : limitCode === 'MEMBERSHIP_USE_EXHAUSTED'
        ? 'template_membership_use'
        : failure?.code === 'SOUL_ID_SUBSCRIPTION_REQUIRED' ? 'soul_id_creation' : undefined;
  if (typeof window !== 'undefined' && limitCode) {
    window.dispatchEvent(new CustomEvent('membership-quota-changed', {
      detail: { code: limitCode, source, scopeKey: detail?.scope_key, projectId: detail?.project_id },
    }));
  }
  pricingModal.open({
    triggerReason: 'feature_locked',
    quotaType: source ?? getSubscriptionEntitlementFeatureKey(params.error),
    featureLabel: params.featureLabel,
    membershipRequirement: source === 'soul_id_creation' ? { kind: 'feature', key: 'soul_id_creation' }
      : source === 'project_limit' ? { kind: 'project' }
      : source === 'project_item_limit' && typeof detail?.project_id === 'string' ? { kind: 'project_item', projectId: detail.project_id }
      : source === 'template_membership_use' && typeof detail?.scope_key === 'string'
        ? { kind: 'template', key: detail.scope_key }
      : !source && getSubscriptionEntitlementFeatureKey(params.error)
        ? { kind: typeof detail?.feature_key === 'string' ? 'feature' : 'model', key: getSubscriptionEntitlementFeatureKey(params.error)! } : undefined,
    defaultTab: 'upgrade',
    source,
    planSurface: 'web',
    eligibleSubscriptionPlanSlugs: Array.isArray(detail?.required_plans) ? detail.required_plans.filter((value): value is string => typeof value === 'string') : undefined,
  });
  return true;
}

/** Credits are a separate purchase intent; topping up never unlocks a feature. */
export function handleCreditApiError(error: unknown): boolean {
  const failure = readEntitlementFailure(error);
  if (entitlementPurchaseIntent(failure) !== 'topup') return false;
  pricingModal.open({
    triggerReason: 'quota_exceeded', quotaType: 'credits', source: 'credits_exhausted',
    defaultTab: 'topup', requiredCredits: entitlementRequiredCredits(failure), availableCredits: entitlementAvailableCredits(failure),
  });
  return true;
}

/** Null means unavailable; a failed check must never be reported as unlocked. */
export async function checkMembershipRequirement(requirement: MembershipRequirement): Promise<boolean | null> {
  if (requirement.kind === 'feature') {
    const snapshot = await getSubscriptionEntitlementSnapshot();
    return snapshot ? snapshot.features[requirement.key] === true : null;
  }
  if (requirement.kind === 'model') {
    const result = await subscriptionsApi.getModelAccess([requirement.key]);
    if (result.success !== true || result.error) return null;
    const allowed = result.data?.items.find(item => item.model_key === requirement.key)?.allowed;
    return typeof allowed === 'boolean' ? allowed : null;
  }
  if (requirement.kind === 'template') {
    const result = await subscriptionsApi.getMembershipUses(requirement.kind, [requirement.key]);
    if (result.success !== true || result.error) return null;
    const item = result.data?.items.find(item => item.scope_key === requirement.key);
    return item && typeof item.locked === 'boolean' ? !item.locked : null;
  }
  const result = requirement.kind === 'project'
    ? await projectApi.getProjectsQuotaStatus()
    : await projectApi.getProjectItemsQuotaStatus(requirement.projectId);
  if (result.success !== true || result.error || !result.data) return null;
  return typeof result.data.locked === 'boolean' ? !result.data.locked : null;
}

/** Invoke only on an explicit submission or a failure-card action. */
export function presentEntitlementFailure(error: unknown): boolean {
  return handleSubscriptionEntitlementApiError({ error }) || handleCreditApiError(error);
}
